import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnDestroy, OnInit, signal, ViewChild, inject } from "@angular/core";
import { AbstractControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { BusyIndicatorComponent, formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { LoggingService } from "@tylertech/core-module";
import { LgdFormControlFactory, LgdSelectComponent } from "@tylertech/lgd-reactive-forms";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria, ProfileSettingViewModel, SettingLookupOption, } from "@tylertech/tyler-api-types";
import { ChartConfigurationCustomTypesPerDataset, ChartEvent, ChartType, LegendElement, LegendItem } from "chart.js";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { distinct, takeUntil } from "rxjs/operators";
import { LayoutDirective, FlexDirective, LayoutAlignDirective } from "@ngbracket/ngx-layout/flex";
import { ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeTooltipModule } from "@tylertech/forge-angular";

const enum ParameterName
{
    ConsumptionChartVendorServiceType = "ConsumptionChartVendorServiceType"
}

const enum AdditionalChartOptions
{
    DefaultSelectionForServiceType = "DefaultSelectionForServiceType"
}

@Component({
    selector: "utilities-prepaid-consumption-chart",
    templateUrl: "./prepaidConsumptionChart.tmpl.html",
    styleUrls: [
        "./prepaidConsumptionChart.component.scss"
    ],
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeTooltipModule,
        LgdSelectComponent,
        ReactiveFormsModule,
    ]
})
export class PrepaidConsumptionChartComponent implements OnInit, OnDestroy
{
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartService = inject(ChartService);

    private readonly destructor$ = new Subject<void>();

    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfigurationCustomTypesPerDataset;

    private _chartCriteria: IChartCriteria;

    private readonly serviceTypesSubject = new BehaviorSubject<Array<SettingLookupOption>>([]);

    readonly formGroup: FormGroup;

    isLoaded: boolean;
    errorMessage: string | undefined;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });

        this.formGroup = new FormGroup({
            serviceType: LgdFormControlFactory.select<SettingLookupOption>(
                undefined,
                false,
                {
                    displayValueFn: x => x?.Display
                }),
        });
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined { return this._chartCriteria; }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    private get serviceTypeControl(): AbstractControl<SettingLookupOption> { return this.formGroup.get("serviceType"); }
    private get selectedServiceType(): SettingLookupOption { return this.serviceTypeControl.value; }

    get serviceTypesObservable(): Observable<Array<SettingLookupOption>> { return this.serviceTypesSubject.asObservable(); }

    get chartHasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        (this.chartHasData
            ? new Promise(() =>
            {
                this.updateServiceTypeControl();
            })
            : this.loadChartData())
            .then(
                () =>
                {
                    this.updateChartConfiguration();
                }, e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });

        subscribeToValueChanges(this.serviceTypeControl, (_newValue: SettingLookupOption) =>
        {
            this.addServiceTypeParameterToCriteria();

            this.loadChartData();
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(
                takeUntil(self.destructor$),
                distinct())
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    this.errorMessage = undefined;

                    this.updateServiceTypeControl();
                })
            .then(
                () =>
                {
                    if (!this.chartHasData)
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.errorMessage = error.error.Message;
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private updateServiceTypeControl(): void
    {
        const serviceTypes = this.chartCriteria.Parameters
            .find(x => x.Id === ParameterName.ConsumptionChartVendorServiceType)?.Options;
        if (serviceTypes)
        {
            this.serviceTypesSubject.next(serviceTypes);
            this.serviceTypeControl.setValue(serviceTypes[0], { emitEvent: false });
        }
    }

    private addServiceTypeParameterToCriteria(): void
    {
        let serviceTypeValue = this.chartCriteria.Parameters?.find(x => x.Id === ParameterName.ConsumptionChartVendorServiceType) as ProfileSettingViewModel;
        if (!serviceTypeValue)
        {
            serviceTypeValue = {
                Id: ParameterName.ConsumptionChartVendorServiceType,
            };
            this.chartCriteria?.Parameters?.push(serviceTypeValue);
        }
        serviceTypeValue.Value = this.serviceTypeControl.value;
    }

    private updateChartConfiguration(): void
    {
        this.addServiceTypeParameterToCriteria();
        this.displayChart();

        const defaultServiceTypeValue = this.chartCriteria.Parameters
            .find(x => x.Id === ParameterName.ConsumptionChartVendorServiceType)
            ?.AdditionalAttributes[AdditionalChartOptions.DefaultSelectionForServiceType] as string;
        if (defaultServiceTypeValue
            && this.selectedServiceType?.Value !== defaultServiceTypeValue)
        {
            const serviceType = this.serviceTypesSubject.value?.find(x => x.Value === defaultServiceTypeValue);
            this.serviceTypeControl.setValue(serviceType, { emitEvent: false });
        }
    }

    private displayChart(): void
    {
        const chartColors = this.$chartService.getColors(3);
        this.chartConfig = {
            options: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12 },
                        onClick: (_event: ChartEvent, legendItem: LegendItem, legend: LegendElement<ChartType>): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const meta = legend.chart.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = !meta.hidden ? !legend.chart.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (legend.chart.data.datasets || []).length; ++i)
                            {
                                const iMeta = legend.chart.getDatasetMeta(i);
                                if (!iMeta.hidden && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            legend.chart.options.scales[yAxisID].display = showScale;
                            legend.chart.update();
                        },
                    },
                },
                elements: { line: {} },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    leftYAxis: {
                        type: "linear",
                        display: true,
                        position: "left",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(
                                    value > -10 && value < 10
                                        ? value.toFixed(2)
                                        : value.toFixed(0));
                            },
                        },
                    },
                    rightYAxis: {
                        type: "linear",
                        display: true,
                        position: "right",
                        grid: { display: false },
                        beginAtZero: true,
                        ticks: {
                            callback: (value: number) => value,
                        },
                    },
                },
            },
            data: {
                datasets: this.chartCriteria.ChartDatas.length === 3
                    ? [
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[0].Data,
                            backgroundColor: chartColors[0],
                            borderColor: chartColors[0],
                            hoverBackgroundColor: chartColors[0],
                            hoverBorderColor: chartColors[0],
                            yAxisID: "leftYAxis",
                            categoryPercentage: 0.5,
                            barPercentage: 0.8,
                        },
                        {
                            type: "line",
                            label: `${this.chartCriteria.ChartDatas[1].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[1].Data,
                            borderColor: chartColors[1],
                            backgroundColor: chartColors[1],
                            yAxisID: "rightYAxis",
                        },
                        {
                            type: "line",
                            label: `${this.chartCriteria.ChartDatas[2].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[2].Data,
                            fill: false,
                            borderColor: chartColors[2],
                            backgroundColor: chartColors[2],
                            pointBorderColor: chartColors[2],
                            pointBackgroundColor: chartColors[2],
                            pointHoverBackgroundColor: chartColors[2],
                            pointHoverBorderColor: chartColors[2],
                            yAxisID: "rightYAxis",
                        }
                    ]
                    : [],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }

    displayChartParameterDialogAndReload(): void
    {
        this.$chartDialogService.show(this.chartCriteria.Parameters, "Prepaid Consumption")
            .then((result: ProfileSettingViewModel[]) =>
            {
                if (!Utilities.arraysEqual(this.chartCriteria.Parameters, result))
                {
                    this.chartCriteria.Parameters = result;
                    return this.loadChartData();
                }
            });
    }
}
